import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSearch,
    FaDice,
    FaPrint,
    FaUserTie,
    FaBuilding
} from 'react-icons/fa';

const EngineerSubheGenerateLottery = () => {
    const navigate = useNavigate();
    const [allWorks, setAllWorks] = useState({});
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [lotteryResults, setLotteryResults] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [lotterySaved, setLotterySaved] = useState(false);

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);

        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');

        let engData = {};
        let socData = {};
        let worksData = {};
        let engDone = false;
        let socDone = false;
        let worksDone = false;

        const processResults = () => {
            if (engDone && socDone && worksDone) {
                const combined = [
                    ...Object.entries(engData).map(([id, val]) => ({ 
                        id, 
                        ...val, 
                        type: 'Engineer',
                        displayName: val.firstName ? `${val.firstName} ${val.surname}` : (val.fullName || val.name || "-")
                    })),
                    ...Object.entries(socData).map(([id, val]) => ({ 
                        id, 
                        ...val, 
                        type: 'Society',
                        displayName: val.societyName || val.fullName || val.name || "-"
                    }))
                ];

                // Filter for those who have selected works (work_payment exists)
                const interestedUsers = combined.filter(user => user.work_payment);
                
                // Sort by name
                interestedUsers.sort((a, b) => a.displayName.localeCompare(b.displayName));

                // Extract already saved lottery results for today
                const today = new Date();
                const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
                const savedResults = {};

                interestedUsers.forEach(user => {
                    if (user.GeneratedLottery && user.GeneratedLottery[dateStr]) {
                        const lotteryData = user.GeneratedLottery[dateStr];
                        savedResults[user.id] = lotteryData.workNumber || lotteryData.id || lotteryData.workCode;
                    }
                });

                if (Object.keys(savedResults).length > 0) {
                    setLotteryResults(savedResults);
                }

                setAllWorks(worksData);
                setRegistrations(interestedUsers);
                setLoading(false);
            }
        };

        onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            processResults();
        });

        onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            processResults();
        });

        onValue(worksRef, (snapshot) => {
            worksData = snapshot.val() || {};
            worksDone = true;
            processResults();
        });

    }, []);

    const filteredUsers = useMemo(() => {
        return registrations.filter(user => 
            user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.taluka && user.taluka.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [registrations, searchTerm]);

    // Flatten data for a single unified table (Deduplicated per user)
    const tableData = useMemo(() => {
        return filteredUsers.map(user => {
            let selectedCodes = [];
            if (user.work_payment) {
                Object.values(user.work_payment).forEach(data => {
                    if (data.selectedWorks) {
                        data.selectedWorks.forEach(workStr => {
                            // Extract code: parts before " - "
                            const code = workStr.split(' - ')[0];
                            if (code && !selectedCodes.includes(code)) {
                                selectedCodes.push(code);
                            }
                        });
                    }
                });
            }
            return {
                userId: user.id,
                displayName: user.displayName,
                type: user.type,
                phone: user.whatsappNo || user.mobile || "-",
                taluka: user.taluka || "-",
                works: selectedCodes,
                worksString: selectedCodes.join(', ')
            };
        });
    }, [filteredUsers]);

    const handleGenerateLottery = async () => {
        setIsGenerating(true);
        setLotterySaved(false);
        
        // Brief delay for visual effect
        setTimeout(async () => {
            // 1. Prepare users list
            const eligibleUsers = [...tableData].filter(u => u.works.length > 0);
            
            // 2. Shuffle users for fairness (Fisher-Yates)
            for (let i = eligibleUsers.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [eligibleUsers[i], eligibleUsers[j]] = [eligibleUsers[j], eligibleUsers[i]];
            }

            const assignedWorks = new Set();
            const results = {};
            const dbUpdates = {};
            
            const today = new Date();
            const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

            // 3. Assign works (each user gets at most one work, no duplicates)
            eligibleUsers.forEach(user => {
                // Shuffle user's own choices too for extra randomness
                const userChoices = [...user.works].sort(() => Math.random() - 0.5);
                
                // Find first choice that hasn't been taken by someone else
                const winnerCode = userChoices.find(code => !assignedWorks.has(code));
                
                if (winnerCode) {
                    results[user.userId] = winnerCode;
                    assignedWorks.add(winnerCode);

                    // Find full work details from allWorks
                    let workDataObject = { workCode: winnerCode };
                    
                    // Search in allWorks for the object that has the matching code
                    Object.values(allWorks).forEach(catOrWork => {
                        if (catOrWork.workName) { // Direct work object
                            if (catOrWork.id === winnerCode || catOrWork.workNumber === winnerCode) {
                                workDataObject = { ...catOrWork };
                            }
                        } else if (typeof catOrWork === 'object') { // Category object
                            Object.values(catOrWork).forEach(subWork => {
                                if (subWork.id === winnerCode || subWork.workNumber === winnerCode) {
                                    workDataObject = { ...subWork };
                                }
                            });
                        }
                    });

                    // Prepare DB Update
                    const path = `ekamwatapzp/${user.type}Registrations/${user.userId}/GeneratedLottery/${dateStr}`;
                    dbUpdates[path] = {
                        ...workDataObject,
                        generatedAt: new Date().toISOString()
                    };
                } else {
                    results[user.userId] = null;
                }
            });

            // 4. Filter and move only relevant works (सुबे/मजूर) to generatework node and clear from WorkNames
            const worksToMove = {};
            
            Object.entries(allWorks).forEach(([meetingId, meetingData]) => {
                if (meetingData && typeof meetingData === 'object') {
                    if (meetingData.schemeName) { // Direct work object
                        if (meetingData.sharedwork === "सुबे" || meetingData.sharedwork === "मजूर") {
                            worksToMove[meetingId] = meetingData;
                            dbUpdates[`ekamwatapzp/WorkNames/${meetingId}`] = null;
                        }
                    } else { // Category/Meeting object containing works
                        Object.entries(meetingData).forEach(([workId, workData]) => {
                            if (workData && typeof workData === 'object' && (workData.sharedwork === "सुबे" || workData.sharedwork === "मजूर")) {
                                if (!worksToMove[meetingId]) worksToMove[meetingId] = {};
                                worksToMove[meetingId][workId] = workData;
                                dbUpdates[`ekamwatapzp/WorkNames/${meetingId}/${workId}`] = null;
                            }
                        });
                    }
                }
            });

            dbUpdates[`ekamwatapzp/generatework/${dateStr}`] = worksToMove;

            try {
                // 4. Update all results at once in Firebase
                await update(ref(db), dbUpdates);
                setLotteryResults(results);
                setLotterySaved(true);
            } catch (error) {
                console.error("Firebase update failed:", error);
                alert("लॉटरी जतन करताना त्रुटी आली.");
            } finally {
                setIsGenerating(false);
            }
        }, 1500);
    };

    const handleResetLottery = async () => {
        if (!window.confirm("तुम्हाला खात्री आहे की तुम्हाला लॉटरी निकाल रिसेट करायचे आहेत? यामुळे कामांची माहिती पुन्हा मूळ ठिकाणी (WorkNames) हलवली जाईल.")) {
            return;
        }

        setLoading(true);
        const today = new Date();
        const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        const lgRef = ref(db, `ekamwatapzp/generatework/${dateStr}`);
        
        onValue(lgRef, async (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const dbUpdates = {};
                
                // 1. Restore WorkNames from generatework
                Object.entries(data).forEach(([meetingId, meetingData]) => {
                    dbUpdates[`ekamwatapzp/WorkNames/${meetingId}`] = meetingData;
                });
                
                // 2. Clear that specific date node
                dbUpdates[`ekamwatapzp/generatework/${dateStr}`] = null;

                // 3. Clear today's lottery results for all users
                registrations.forEach(user => {
                    if (user.GeneratedLottery && user.GeneratedLottery[dateStr]) {
                        dbUpdates[`ekamwatapzp/${user.type}Registrations/${user.id}/GeneratedLottery/${dateStr}`] = null;
                    }
                });

                try {
                    await update(ref(db), dbUpdates);
                    setLotteryResults({});
                    setLotterySaved(false);
                    // Page will re-load data via existing useEffect listeners
                } catch (error) {
                    console.error("Firebase reset failed:", error);
                    alert("लॉटरी रिसेट करताना त्रुटी आली.");
                } finally {
                    setLoading(false);
                }
            } else {
                alert("रिसेट करण्यासाठी कोणतीही जतन केलेली लॉटरी माहिती आढळली नाही.");
                setLoading(false);
            }
        }, { onlyOnce: true });
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen bg-gray-50/30 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
                        <p className="text-gray-500 font-bold uppercase tracking-widest text-xs animate-pulse">माहिती लोड होत आहे...</p>
                    </div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen font-marathi">
                {/* Premium Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-8 md:p-10 mb-8 shadow-2xl relative overflow-hidden group no-print">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#4338ca]/10 rounded-full -ml-16 -mb-16 blur-2xl"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6 text-center md:text-left">
                            <button 
                                onClick={() => navigate('/engineer/subhe-interested-works-menu')}
                                className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-2xl transition-all hover:scale-110 active:scale-90 backdrop-blur-sm border border-white/10"
                            >
                                <FaArrowLeft size={18} />
                            </button>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">लॉटरी जनरेट करा (सर्व अर्जदार)</h1>
                                <p className="text-gray-400 font-bold text-sm tracking-widest uppercase flex items-center justify-center md:justify-start gap-2">
                                    <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                                    एकूण अर्जदार: {toMarathiNumber(tableData.length)}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                             <div className="relative group/search">
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/search:text-indigo-500 transition-colors" />
                                <input 
                                    type="text"
                                    placeholder="नाव किंवा तालुका शोधा..."
                                    className="pl-12 pr-6 py-4 bg-white/10 border border-white/10 rounded-2xl text-white placeholder-gray-500 outline-none focus:bg-white/20 focus:border-white/20 transition-all w-full md:w-80 backdrop-blur-md"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button 
                                onClick={() => window.print()}
                                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-2xl font-bold border border-white/10 backdrop-blur-md transition transform active:scale-95 flex items-center justify-center gap-2"
                            >
                                <FaPrint /> प्रिंट
                            </button>
                            <button 
                                onClick={handleResetLottery}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-2xl font-bold shadow-lg shadow-amber-900/20 transition transform active:scale-95 flex items-center justify-center gap-2 min-w-[200px]"
                            >
                                <FaDice /> लॉटरी रिसेट करा 
                            </button>
                            <button 
                                onClick={handleGenerateLottery}
                                disabled={isGenerating}
                                className={`bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-2xl font-bold shadow-lg shadow-rose-900/20 transition transform active:scale-95 flex items-center justify-center gap-2 min-w-[200px] ${isGenerating ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isGenerating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        लॉटरी जनरेट होत आहे...
                                    </>
                                ) : (
                                    <><FaDice /> लॉटरी जनरेट करा</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Success Notification */}
                {lotterySaved && (
                    <div className="mb-6 bg-emerald-50 border border-emerald-100 p-4 rounded-2xl flex items-center justify-between text-emerald-700 animate-in fade-in slide-in-from-top duration-500">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                                <FaDice />
                            </div>
                            <span className="font-bold">लॉटरी यशस्वीरित्या जनरेट झाली आणि जतन केली गेली!</span>
                        </div>
                        <button onClick={() => setLotterySaved(false)} className="text-emerald-400 hover:text-emerald-600 font-bold p-2">×</button>
                    </div>
                )}

                {/* Print Header (Visible only on print) */}
                <div className="hidden print:block mb-8 text-center border-b-2 border-gray-800 pb-4">
                    <h1 className="text-2xl font-bold">जिल्हा परिषद लॉटरी अहवाल</h1>
                    <p className="text-sm font-bold mt-1">सर्व अर्जदार आणि त्यांनी निवडलेल्या कामांची यादी</p>
                </div>

                {/* Table Content */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1100px]">
                        <thead>
                            <tr className="bg-[#1a1c23] text-white">
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10 w-16 text-center">#</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10">अर्जदाराचे नाव</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10 w-24 text-center">प्रकार</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10 w-32">तालुका</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10 w-40">मोबाईल</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest border-r border-white/10">निवडलेल्या कामाचा आयडी (Code)</th>
                                <th className="px-6 py-5 text-[11px] font-black uppercase tracking-widest text-rose-400 bg-rose-500/5 min-w-[150px] text-center">लॉटरी निकाल (Winner)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {tableData.length > 0 ? tableData.map((row, idx) => {
                                const winnerCode = lotteryResults[row.userId];
                                return (
                                    <tr key={idx} className={`hover:bg-gray-50 transition-colors group ${winnerCode ? 'bg-rose-50/30' : ''}`}>
                                        <td className="px-6 py-5 text-sm font-bold text-gray-400 text-center border-r border-gray-50">
                                            {toMarathiNumber(idx + 1)}
                                        </td>
                                        <td className="px-6 py-5 border-r border-gray-50">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${row.type === 'Engineer' ? 'bg-indigo-50 text-indigo-600' : 'bg-teal-50 text-teal-600'}`}>
                                                    {row.type === 'Engineer' ? <FaUserTie /> : <FaBuilding />}
                                                </div>
                                                <span className="font-bold text-gray-800">{row.displayName}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-center border-r border-gray-50">
                                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-tighter ${row.type === 'Engineer' ? 'text-indigo-600' : 'text-teal-600'}`}>
                                                {row.type === 'Engineer' ? 'अभियंता' : 'संस्था'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 border-r border-gray-50">
                                            <span className="text-xs font-bold text-gray-600">{row.taluka}</span>
                                        </td>
                                        <td className="px-6 py-5 border-r border-gray-50">
                                            <span className="text-xs font-bold text-gray-600">{row.phone}</span>
                                        </td>
                                        <td className="px-6 py-5 border-r border-gray-50">
                                            <div className="flex flex-wrap gap-2">
                                                {row.worksString.split(', ').map((code, cIdx) => (
                                                    <span key={cIdx} className={`px-3 py-1 rounded-lg border text-xs font-extrabold shadow-sm ${winnerCode === code ? 'bg-rose-600 text-white border-rose-500 scale-110' : 'bg-indigo-50 text-indigo-600 border-indigo-100'}`}>
                                                        {code}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-center bg-rose-500/5">
                                            {winnerCode ? (
                                                <div className="flex flex-col items-center justify-center">
                                                    <span className="text-lg font-black text-rose-600">{winnerCode}</span>
                                                    <span className="text-[10px] font-black text-rose-400 uppercase tracking-tighter">विजेता</span>
                                                </div>
                                            ) : Object.keys(lotteryResults).length > 0 ? (
                                                <span className="text-[10px] font-bold text-gray-300 uppercase italic">शिल्लक नाही</span>
                                            ) : (
                                                <span className="text-[10px] font-bold text-gray-300 uppercase">-</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-20 text-center text-gray-400 font-bold">
                                        कोणतीही माहिती उपलब्ध नाही.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    .print\\:block { display: block !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .p-6, .p-8 { padding: 0 !important; }
                    .rounded-3xl { border-radius: 0 !important; border: none !important; }
                    .shadow-2xl, .shadow-sm { box-shadow: none !important; }
                    table { border: 1px solid #eee !important; width: 100% !important; }
                    th { background: #f8fafc !important; color: black !important; border: 1px solid #ddd !important; -webkit-print-color-adjust: exact; }
                    td { border: 1px solid #eee !important; }
                    .bg-indigo-50 { background: transparent !important; }
                    .bg-rose-500\\/5 { background: transparent !important; }
                    .text-rose-600 { color: black !important; font-weight: bold !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheGenerateLottery;
